import React, { useEffect, useState, useCallback } from 'react';
import { getApi } from '../../../utils/services';
import { PETTY_CASH_LIST } from '../../../utils/apiRoutes';
import { usePettyCashColumns } from "../../../utils/helpers/tableHelpers/useFeedColumnPettyCash";
import PettyCashTable from "../../Table/PettyCash";
import { Helmet } from "react-helmet";
import Loader from '../../common/Loader';
import { ADMIN_EXECUTIVE_ID, ADMIN_MANAGER_ID, VENDORID, VP_ID } from '../../../utils/constant';
import { useNavigate } from 'react-router-dom';
import CreateBillIcon from '../../../assets/image/CreateBillIcon';
import moment from 'moment';
import { fetchPettyCashFunds } from '../../../utils/helpers/globalMethod';

const PettyCashList = () => {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem("userData"));
    const [pettyCashList, setPettyCashList] = useState([]);
    const [loader, setLoader] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [globalSearch, setGlobalSearch] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const pageSize = 10;
    const [billType, setBillType] = useState(null);
    const [totalRemainingFund, setTotalRemainingFund] = useState(0);
    const [neededFund, setNeededFund] = useState(0);
    const [showNeededFund, setShowNeededFund] = useState(false);

    const currentDate = new Date();
    const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const currentYear = currentDate.getFullYear();
    const [monthFilter, setMonthFilter] = useState(`${currentYear}-${currentMonth}`);

    const handleCreatePettyCash = () => {
        navigate('/bill-type');
    };

    const handlerPettyCashList = useCallback(
        async (pageNumber, pageSize, searchQuery, monthFilter) => {
            try {
                const billDate = moment(monthFilter, 'YYYY-MM').endOf('month').format('YYYY-MM-DD');
                let routeName;

                if (user.RoleId === VENDORID) {
                    routeName = `${PETTY_CASH_LIST}?vendorId=${user.id}&page=${pageNumber}&pageSize=${pageSize}&globalSearch=${searchQuery}&billDate=${billDate}`;
                } else {
                    routeName = `${PETTY_CASH_LIST}?page=${pageNumber}&pageSize=${pageSize}&globalSearch=${searchQuery}&billDate=${billDate}`;
                }

                setLoader(true);
                let result = await getApi(routeName);
                setLoader(false);

                const pettyCashListData = result.res.pettyCash_list || [];
                setPettyCashList(pettyCashListData);

                if (pettyCashListData.length > 0) {
                    setBillType(pettyCashListData[0].BillType);
                }

                const totalItems = result.res.record_count || 0;
                setTotalPages(Math.ceil(totalItems / pageSize));
            } catch (error) {
                setLoader(false);
                console.error("Error fetching petty cash list:", error);
            }
        },
        [user.RoleId, user.id]
    );


    const refreshTable = useCallback(() => {
        handlerPettyCashList(pageNumber, pageSize, searchQuery, monthFilter);
    }, [handlerPettyCashList, pageNumber, pageSize, searchQuery, monthFilter]);

    const pettyCashColumns = usePettyCashColumns(
        refreshTable,
        pageNumber,
        pageSize,
        searchQuery,
        monthFilter,
        billType
    );

    useEffect(() => {
        const fetchFundsData = async () => {
            try {
                const {
                    totalRemaining,
                    totalNeeded
                } = await fetchPettyCashFunds();

                setTotalRemainingFund(totalRemaining);

                if (totalNeeded > 0) {
                    setNeededFund(totalNeeded);
                    setShowNeededFund(true);
                } else {
                    setShowNeededFund(false);
                }
            } catch (error) {
                console.error("Error fetching funds data:", error);
                setTotalRemainingFund(0);
                setShowNeededFund(false);
            }
        };

        fetchFundsData();
    }, []);

    useEffect(() => {
        if (monthFilter) {
            handlerPettyCashList(pageNumber, pageSize, searchQuery, monthFilter);
        }
    }, [handlerPettyCashList, pageNumber, pageSize, searchQuery, monthFilter]);

    const handlePageChange = (page) => {
        setPageNumber(page);
        navigate('/petty-cash-listing');
    };

    const handleSearchSubmit = () => {
        if (globalSearch !== searchQuery) {
            setSearchQuery(globalSearch);
            setPageNumber(1);
            navigate('/petty-cash-listing');
        } else {
            handlerPettyCashList(pageNumber, pageSize, searchQuery, monthFilter);
        }
    };

    const handleMonthYearChange = (newMonthYear) => {
        if (newMonthYear) {
            setMonthFilter(newMonthYear);
            setPageNumber(1);
        }
    };

    const handleAddFund = () => {
        navigate(`/petty-cash-funds`);
    }

    return (
        <>
            <Helmet>
                <title>Vendor Portal || Petty Cash List</title>
            </Helmet>

            <Loader isLoading={loader} />

            <div className="container-section">
                <div className="section-header section-header-btn-box d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex align-items-center">
                        <h3>Petty Cash Bills</h3>
                    </div>
                    <div className="d-flex align-items-center">
                        {(user?.RoleId === ADMIN_EXECUTIVE_ID || user?.RoleId === ADMIN_MANAGER_ID) && (
                            <div className="wallet-container position-relative me-3">


                                {showNeededFund ? (
                                    <>
                                        <span className="wallet-label">Total Available Funds</span>
                                        <span className="wallet-amount text-danger">
                                            - ₹ {neededFund.toFixed(2)}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <span className="wallet-label">Total Available Funds</span>
                                        <span className="wallet-amount text-success">
                                            ₹ {totalRemainingFund.toFixed(2)}
                                        </span>
                                    </>
                                )}

                            </div>
                        )}
                        {(user?.RoleId === ADMIN_EXECUTIVE_ID || user?.RoleId === ADMIN_MANAGER_ID || user?.RoleId === VENDORID) && (
                            <div className="create-btn">
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={handleCreatePettyCash}
                                >
                                    <CreateBillIcon />
                                    <span className="ms-2">Create</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="form-style">
                    <PettyCashTable
                        columns={pettyCashColumns}
                        data={pettyCashList}
                        pageNumber={pageNumber}
                        setPageNumber={setPageNumber}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        globalSearch={globalSearch}
                        setGlobalSearch={setGlobalSearch}
                        handlerPettyCashList={handlerPettyCashList}
                        pageSize={pageSize}
                        handleSearchSubmit={handleSearchSubmit}
                        billType={billType}
                        monthFilter={monthFilter}
                        onMonthYearChange={handleMonthYearChange}
                    />
                </div>
            </div>
        </>
    );
};

export default PettyCashList;